import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/format.js';
import { Spinner } from './Spinner.jsx';

/**
 * The one button. Denser than the storefront's — this is a tool, and a toolbar
 * with 44px buttons wastes the row an operator wants for data.
 */
const base =
  'inline-flex items-center justify-center gap-1.5 rounded font-medium ' +
  'transition-colors duration-100 focus-visible:outline-2 focus-visible:outline-offset-1 ' +
  'disabled:cursor-not-allowed disabled:opacity-45 select-none whitespace-nowrap';

const variants = {
  primary: 'bg-ink text-white hover:bg-ink/88 focus-visible:outline-ink',
  outline: 'border border-mist bg-surface text-ink hover:border-muted hover:bg-paper focus-visible:outline-signal',
  ghost: 'text-muted hover:bg-mist/60 hover:text-ink focus-visible:outline-signal',
  danger: 'bg-danger text-white hover:bg-danger/88 focus-visible:outline-danger',
  subtle: 'bg-mist/60 text-ink hover:bg-mist focus-visible:outline-signal',
};

const sizes = {
  xs: 'h-7 px-2 text-2xs',
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-9 px-3.5 text-xs',
  lg: 'h-10 px-4 text-sm',
  icon: 'size-8 p-0',
};

export const Button = forwardRef(function Button(
  { to, href, variant = 'primary', size = 'md', loading, loadingLabel = 'Working', fullWidth, className, children, disabled, ...props },
  ref,
) {
  const classes = cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className);

  if (to) return <Link ref={ref} to={to} className={classes} {...props}>{children}</Link>;
  if (href) return <a ref={ref} href={href} className={classes} {...props}>{children}</a>;

  return (
    <button
      ref={ref}
      type={props.type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Spinner className="size-3.5" />}
      {loading ? loadingLabel : children}
    </button>
  );
});

export default Button;
